// src/services/avatarItemsService.js
//
// Avatar items you UPLOAD in the Layout Builder (Rewards & avatar settings →
// 🧥 Upload avatar items): jackets & shirts, sneakers, mustaches & beards,
// everyday (Level 1–4) or in a holiday season, free or for coins.
//
//   Firestore  avatarItems/{id}     id "av-up-<name>-<time>"
//              { kind: "top"|"shoes"|"mustache", name, price, level, season,
//                img, path, ar, sh, arms, hem, pair, fit, layer, keepColor,
//                hidden, active, sort, createdAt }
//   Storage    topic-images/avatar-items/<id>.png   (public read, admin write)
//
// Everyone signed in reads the list (so classmates' avatars draw the item);
// only admins write it (firestore.rules). The server (functions/shop.js)
// reads the same documents for the price and the season when a student buys
// one, so a price can never come from the browser.

import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase/firebaseConfig";
import { avatarItemsVersion, onAvatarItemsChange, setLiveAvatarItems } from "../components/avatar/avatarCatalog";

export const AVATAR_ITEMS = "avatarItems";

let started = false;
let latest = [];
const listListeners = new Set();

const start = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  let unsub = null;
  onAuthStateChanged(getAuth(), (user) => {
    if (unsub) {
      unsub();
      unsub = null;
    }
    if (!user) return;
    unsub = onSnapshot(
      collection(db, AVATAR_ITEMS),
      (snap) => {
        latest = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLiveAvatarItems(latest);
        listListeners.forEach((f) => f(latest));
      },
      () => {
        /* rules not deployed yet — the built-in items still work */
      }
    );
  });
};

/** Re-renders when uploaded items arrive or change (AvatarFace uses it). */
export const useAvatarItemsVersion = () => {
  const [v, setV] = useState(avatarItemsVersion());
  useEffect(() => {
    start();
    setV(avatarItemsVersion());
    return onAvatarItemsChange(setV);
  }, []);
  return v;
};

/** Every uploaded item, hidden and inactive included (the admin list). */
export const useUploadedAvatarItems = () => {
  const [list, setList] = useState(latest);
  useEffect(() => {
    start();
    setList(latest);
    listListeners.add(setList);
    return () => listListeners.delete(setList);
  }, []);
  return list;
};

const slugify = (s) =>
  String(s || "item")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30) || "item";

export const newAvatarItemId = (name) => `av-up-${slugify(name)}-${Date.now().toString(36)}`;

/** Upload the finished (cut-out) picture; resolves { url, path }. */
export const uploadAvatarItemPicture = async (id, blob) => {
  const path = `topic-images/avatar-items/${id}.png`;
  const r = storageRef(storage, path);
  await uploadBytes(r, blob, { contentType: "image/png", cacheControl: "public, max-age=31536000" });
  return { url: await getDownloadURL(r), path };
};

const clean = (it) => {
  const out = {
    kind: ["top", "shoes", "mustache"].includes(it.kind) ? it.kind : "top",
    name: String(it.name || "New item").slice(0, 60),
    price: Math.max(0, Math.floor(Number(it.price) || 0)),
    level: [1, 2, 3, 4].includes(Number(it.level)) ? Number(it.level) : 1,
    season: String(it.season || ""),
    img: String(it.img || ""),
    path: String(it.path || ""),
    ar: Number(it.ar) || 1,
    hidden: Boolean(it.hidden),
    active: it.active !== false,
    sort: Number(it.sort) || Date.now() / 1e9,
  };
  if (out.kind === "top") {
    out.sh = Math.max(0, Math.min(0.6, Number(it.sh) || 0.16));
    if (it.arms === "short") out.arms = "short";
  }
  if (out.kind === "shoes") {
    out.hem = Math.max(0, Math.min(0.9, Number.isFinite(Number(it.hem)) ? Number(it.hem) : 0.4));
    if (it.pair) out.pair = true;
  }
  if (out.kind === "mustache") {
    const f = Array.isArray(it.fit) ? it.fit : [53.4, 20];
    out.fit = [Math.round(Number(f[0]) * 10) / 10 || 53.4, Math.round(Number(f[1]) * 10) / 10 || 20];
    out.layer = it.layer === "over" ? "over" : "under";
    if (it.keepColor) out.keepColor = true;
  }
  return out;
};

/** Create or update one uploaded item. */
export const saveAvatarItem = async (id, item) => {
  const user = getAuth().currentUser;
  const data = { ...clean(item), updatedAt: serverTimestamp(), updatedBy: user?.email || user?.uid || "" };
  if (item.isNew) data.createdAt = serverTimestamp();
  await setDoc(doc(db, AVATAR_ITEMS, id), data, { merge: true });
  return { id, ...data };
};

/** Small changes from the list (name, price, level, season, hidden). */
export const updateAvatarItem = (id, patch) => setDoc(doc(db, AVATAR_ITEMS, id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });

/** Delete for good (students who bought it lose it from their look). */
export const deleteAvatarItem = async (item) => {
  await deleteDoc(doc(db, AVATAR_ITEMS, item.id));
  if (item.path) {
    try {
      await deleteObject(storageRef(storage, item.path));
    } catch {
      /* already gone */
    }
  }
};
